import { lazy, Suspense, useEffect, useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { getRemoteControlPolicy, RemoteTestPanel, type RemoteLeaderAction } from './components/RemoteTestPanel'
import {
  disablePartyAudio,
  enablePartyAudio,
  getPartyAudioState,
  isPartyAudioEnabled,
  isPartyMusicEnabled,
  isPartySfxEnabled,
  playPartySound,
  restorePartyAudio,
  setPartyMusicEnabled,
  setPartySfxEnabled,
  setPartyAudioScene,
  suspendPartyAudio,
} from './audio'
import type { FirebaseServices } from './firebase'
import { getFirebaseServices } from './firebase'
import {
  adjustBalance,
  adjustPot,
  awardPot,
  contributeChips,
  createGame,
  foldHand,
  getHandResetSummary,
  joinGame,
  recoverTableAuthority,
  readableFirebaseError,
  resetCurrentHand,
  restartGame,
  rotateRecoveryCode,
  setLeader,
  startGame,
  startNextBettingRound,
  startOverGame,
  startNewHand,
  undoLastAction,
} from './gameService'
import { useLiveGame } from './hooks/useLiveGame'
import { useRemoteHarness } from './hooks/useRemoteHarness'
import { LanguageProvider, LanguageToggle, text, useLanguage } from './i18n'
import {
  actionIcon,
  actionText,
  bettingRound,
  formatChips,
  gamePhase,
  getUndoEligibility,
  initials,
  normalizeRecoveryCode,
  normalizeRoomCode,
  parseChipAmount,
  relativeActionTime,
  roundContribution,
} from './lib/domain'
import type { Game, GameAction, GameSession, Member, Player } from './types'
import type { RemoteHarnessState } from './harness/remoteTypes'

const LEGACY_SESSION_KEY = 'poker-pot-live.session.v1'
const TABLE_SESSION_KEY = 'poker-pot-live.table-session.v2'
const PLAYER_SESSION_KEY = 'poker-pot-live.player-session.v2'
const ENABLE_TABLE_RECOVERY = true
const ENABLE_TABLE_UNDO = false
const TestHarnessPage = lazy(() => import('./harness/TestHarnessPage'))
const StandaloneTablePage = lazy(() => import('./tableSim/StandaloneTablePage'))

interface UiMessage { en: string; he: string }
type Notice = UiMessage | { error: unknown }
const uiMessage = (en: string, he: string): UiMessage => ({ en, he })
interface PartyMoment { id: string; kind: 'start' | 'round' | 'winner' | 'hand' | 'lobby' | 'leader'; title: UiMessage; subtitle: UiMessage }

function isHostRoute(): boolean {
  return location.pathname === '/host' || location.pathname.startsWith('/host/')
}

function activeSessionKey(): string {
  return isHostRoute() ? TABLE_SESSION_KEY : PLAYER_SESSION_KEY
}

function readSavedTableSession(): GameSession | null {
  try {
    const session = JSON.parse(localStorage.getItem(TABLE_SESSION_KEY) ?? 'null') as GameSession | null
    return session?.view === 'table' && normalizeRoomCode(session.code).length === 4 ? session : null
  } catch {
    return null
  }
}

function readSession(): GameSession | null {
  try {
    const current = localStorage.getItem(activeSessionKey())
    const legacy = localStorage.getItem(LEGACY_SESSION_KEY)
    const session = JSON.parse(current ?? legacy ?? 'null') as GameSession | null
    if (!session) return null
    if (isHostRoute() && session.view !== 'table') return null
    if (!isHostRoute() && session.view !== 'player') return null
    const requestedCode = normalizeRoomCode(new URLSearchParams(location.search).get('room') ?? '')
    if (requestedCode && requestedCode !== session.code) return null
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
    if (isHostRoute() && navigation?.type === 'navigate' && !requestedCode) return null
    return session
  } catch {
    return null
  }
}

function saveSession(session: GameSession | null) {
  localStorage.removeItem(LEGACY_SESSION_KEY)
  if (session) localStorage.setItem(session.view === 'table' ? TABLE_SESSION_KEY : PLAYER_SESSION_KEY, JSON.stringify(session))
  else localStorage.removeItem(activeSessionKey())
}

function Logo({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`brand ${compact ? 'brand--compact' : ''}`}>
      <span className="brand__mark" aria-hidden="true"><span>P</span></span>
      <span className="brand__words"><b>Poker Pot</b><small>LIVE</small></span>
    </div>
  )
}

function Spinner({ label }: { label?: string }) {
  return <div className="loading"><span className="spinner" /><p>{label ?? text('CONNECTING…', 'מתחברים…')}</p></div>
}

function AudioControls({ musicOn, sfxOn, onMusicToggle, onSfxToggle, compact = false }: {
  musicOn: boolean
  sfxOn: boolean
  onMusicToggle: () => void
  onSfxToggle: () => void
  compact?: boolean
}) {
  return <div className={`audio-controls ${compact ? 'audio-controls--compact' : ''}`} role="group" aria-label={text('Table audio', 'שמע השולחן')}>
    <button className={musicOn ? 'active' : ''} aria-pressed={musicOn} aria-label={musicOn ? text('Mute music', 'השתקת מוזיקה') : text('Turn on music', 'הפעלת מוזיקה')} onClick={onMusicToggle}><span aria-hidden="true">♫</span><b>{text('MUSIC', 'מוזיקה')}</b><small>{musicOn ? text('ON', 'פעיל') : text('OFF', 'כבוי')}</small></button>
    <button className={sfxOn ? 'active' : ''} aria-pressed={sfxOn} aria-label={sfxOn ? text('Mute sound effects', 'השתקת אפקטים') : text('Turn on sound effects', 'הפעלת אפקטים')} onClick={onSfxToggle}><span aria-hidden="true">✦</span><b>{text('SFX', 'אפקטים')}</b><small>{sfxOn ? text('ON', 'פעיל') : text('OFF', 'כבוי')}</small></button>
  </div>
}

function partyMomentForAction(action: GameAction): PartyMoment | null {
  if (action.type === 'start_game') return { id: action.id, kind: 'start', title: uiMessage('HERE WE GO!', 'מתחילים!'), subtitle: uiMessage('THE POT IS LIVE', 'הקופה פעילה') }
  if (action.type === 'set_leader') return { id: action.id, kind: 'leader', title: uiMessage(`${action.targetName} IS LEADER!`, `${action.targetName} מוביל/ה!`), subtitle: uiMessage('THE CROWN HAS MOVED', 'הכתר עבר') }
  if (action.type === 'restart_game') return { id: action.id, kind: 'lobby', title: uiMessage('BACK TO THE LOBBY!', 'חוזרים ללובי!'), subtitle: uiMessage('STACKS RESET · SAME CREW', 'היתרות אופסו · אותם שחקנים') }
  if (action.type === 'new_round') return { id: action.id, kind: 'round', title: uiMessage(`ROUND ${action.amount}`, `סבב ${action.amount}`), subtitle: uiMessage('THE POT CARRIES ON', 'הקופה ממשיכה') }
  if (action.type === 'award') return { id: action.id, kind: 'winner', title: uiMessage(`${action.targetName} WINS!`, `${action.targetName} זוכה!`), subtitle: uiMessage(`${formatChips(action.amount)} FROM THE POT`, `${formatChips(action.amount)} מהקופה`) }
  if (action.type === 'new_hand' || action.type === 'reset_hand') return { id: action.id, kind: 'hand', title: uiMessage('NEW HAND!', 'יד חדשה!'), subtitle: action.type === 'reset_hand' ? uiMessage('BROKEN HAND REFUNDED', 'היד התקולה הוחזרה') : uiMessage('FRESH ROUND OF PLAY', 'סיבוב משחק חדש') }
  return null
}

function PartyMomentOverlay({ moment }: { moment: PartyMoment }) {
  return <div className={`party-moment party-moment--${moment.kind}`} role="status" aria-live="polite"><div className="party-moment__burst"/><strong>{text(moment.title.en, moment.title.he)}</strong><span>{text(moment.subtitle.en, moment.subtitle.he)}</span></div>
}

function Icon({ name }: { name: 'phone' | 'table' | 'copy' | 'users' | 'pot' | 'log' | 'settings' | 'more' | 'close' }) {
  const paths: Record<typeof name, ReactNode> = {
    phone: <><rect x="7" y="2" width="10" height="20" rx="2"/><path d="M11 18h2"/></>,
    table: <><ellipse cx="12" cy="12" rx="9" ry="6"/><path d="M6 17l-2 4M18 17l2 4"/></>,
    copy: <><rect x="8" y="8" width="11" height="11" rx="2"/><path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3"/></>,
    users: <><circle cx="9" cy="8" r="3"/><circle cx="17" cy="9" r="2"/><path d="M3 20c0-4 2-6 6-6s6 2 6 6M15 15c4 0 6 2 6 5"/></>,
    pot: <><ellipse cx="12" cy="6" rx="8" ry="3"/><path d="M4 6v8c0 2 3.5 4 8 4s8-2 8-4V6M4 10c0 2 3.5 4 8 4s8-2 8-4"/></>,
    log: <><path d="M5 4h14v16H5zM8 8h8M8 12h8M8 16h5"/></>,
    settings: <><circle cx="12" cy="12" r="3"/><path d="M19 13.5v-3l-2-.7-.7-1.7.9-1.9-2.1-2.1-1.9.9-1.7-.7L10.5 2h-3l-.7 2.3-1.7.7-1.9-.9-2.1 2.1.9 1.9-.7 1.7-2 .7v3l2 .7.7 1.7-.9 1.9 2.1 2.1 1.9-.9 1.7.7.7 2.3h3l.7-2.3 1.7-.7 1.9.9 2.1-2.1-.9-1.9.7-1.7z"/></>,
    more: <><circle cx="5" cy="12" r="1" fill="currentColor"/><circle cx="12" cy="12" r="1" fill="currentColor"/><circle cx="19" cy="12" r="1" fill="currentColor"/></>,
    close: <path d="M6 6l12 12M18 6L6 18"/>,
  }
  return <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>
}

function GameOverflowPanel({ open, panelId, title, subtitle, onClose, children }: {
  open: boolean
  panelId: string
  title: string
  subtitle?: string
  onClose: () => void
  children: ReactNode
}) {
  const closeRef = useRef<HTMLButtonElement>(null)
  const openerRef = useRef<HTMLElement | null>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return
    openerRef.current = document.activeElement as HTMLElement | null
    document.body.classList.add('overflow-panel-open')
    window.setTimeout(() => closeRef.current?.focus(), 0)
    const closeOnEscape = (event: KeyboardEvent) => {
      if (document.querySelector('dialog[open]')) return
      if (event.key === 'Escape') {
        event.preventDefault()
        onCloseRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const panel = closeRef.current?.closest<HTMLElement>('.game-overflow')
      const focusable = [...(panel?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), [href], [tabindex]:not([tabindex="-1"])') ?? [])]
        .filter((element) => element.offsetParent !== null)
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => {
      window.removeEventListener('keydown', closeOnEscape)
      document.body.classList.remove('overflow-panel-open')
      openerRef.current?.focus()
    }
  }, [open])

  if (!open) return null
  return <div className="overflow-scrim" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
    <section id={panelId} className="game-overflow" role="dialog" aria-modal="true" aria-labelledby={`${panelId}-title`}>
      <header className="game-overflow__header">
        <div><span>{subtitle}</span><h2 id={`${panelId}-title`}>{title}</h2></div>
        <button ref={closeRef} className="icon-button" aria-label={text('Close options', 'סגירת אפשרויות')} onClick={onClose}><Icon name="close"/></button>
      </header>
      <div className="game-overflow__body">{children}</div>
    </section>
  </div>
}

function OverflowSection({ title, icon, children, tone = 'normal' }: {
  title: string
  icon?: ReactNode
  children: ReactNode
  tone?: 'normal' | 'danger'
}) {
  return <section className={`overflow-section ${tone === 'danger' ? 'overflow-section--danger' : ''}`}>
    <h3>{icon}{title}</h3>
    {children}
  </section>
}

function AppContent() {
  useLanguage()
  const [services, setServices] = useState<FirebaseServices | null>(null)
  const [session, setSession] = useState<GameSession | null>(() => readSession())
  const [bootError, setBootError] = useState<unknown>(null)

  useEffect(() => {
    getFirebaseServices().then(setServices).catch(setBootError)
  }, [])

  const updateSession = (next: GameSession | null) => {
    if (!next) disablePartyAudio()
    saveSession(next)
    setSession(next)
  }

  if (bootError) return <FatalState message={readableFirebaseError(bootError)} roomCode={session?.code} onLeaveRoom={session ? () => updateSession(null) : undefined} />
  if (!services) return <main className="splash"><Logo /><Spinner /></main>
  if (!session) return <Welcome services={services} onJoined={updateSession} />
  return <GameRoom key={session.code} services={services} session={session} onSessionChange={updateSession} />
}

function FatalState({ message, roomCode, onLeaveRoom }: { message: string; roomCode?: string; onLeaveRoom?: () => void }) {
  const [showLeave, setShowLeave] = useState(false)
  return (
    <main className="splash">
      <Logo />
      <LanguageToggle/>
      <section className="fatal">
        <b>{text('CAN’T OPEN ROOM', 'אי אפשר לפתוח את החדר')}</b>
        <p>{message}</p>
        <div className="fatal-actions">
          <button className="button button--primary" onClick={() => location.reload()}>{text('RETRY', 'נסו שוב')}</button>
          {roomCode && onLeaveRoom && <button className="button button--ghost" onClick={() => setShowLeave(true)}>{text('LEAVE ROOM / NEW GAME', 'יציאה מהחדר / משחק חדש')}</button>}
        </div>
      </section>
      {showLeave && roomCode && onLeaveRoom && (
        <RoomCodeConfirmation
          code={roomCode}
          title={text('LEAVE STUCK ROOM?', 'לצאת מהחדר התקוע?')}
          description={text('This only forgets the room on this device. The old room and its history stay saved.', 'הפעולה רק מסירה את החדר מהמכשיר הזה. החדר הישן והיומן שלו נשארים שמורים.')}
          confirmLabel={text('LEAVE & START NEW', 'יציאה ומשחק חדש')}
          onCancel={() => setShowLeave(false)}
          onConfirm={onLeaveRoom}
        />
      )}
    </main>
  )
}

function Welcome({ services, onJoined }: { services: FirebaseServices; onJoined: (session: GameSession) => void }) {
  const hostRoute = isHostRoute()
  const [tab, setTab] = useState<'join' | 'create' | 'recover'>(hostRoute ? 'create' : 'join')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const queryCode = normalizeRoomCode(new URLSearchParams(location.search).get('room') ?? '')
  const resumableTable = hostRoute ? readSavedTableSession() : null

  const resumeTable = async () => {
    if (!resumableTable) return
    setPending(true)
    setError(null)
    try {
      await enablePartyAudio('lobby')
      onJoined(resumableTable)
    } catch (reason) {
      disablePartyAudio()
      setError(reason)
    } finally {
      setPending(false)
    }
  }

  const submitJoin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setPending(true)
    setError(null)
    const data = new FormData(event.currentTarget)
    const code = normalizeRoomCode(String(data.get('code') ?? ''))
    try {
      const member = await joinGame(services.db, services.user.uid, {
        code,
        displayName: String(data.get('displayName') ?? ''),
      })
      onJoined({ code, view: member.isPlayer ? 'player' : 'table' })
    } catch (reason) {
      setError(reason)
    } finally {
      setPending(false)
    }
  }

  const submitCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setPending(true)
    setError(null)
    const data = new FormData(event.currentTarget)
    const startingStack = Number(data.get('startingStack'))
    try {
      if (hostRoute) {
        try { await enablePartyAudio('lobby') } catch { /* Lobby still works muted. */ }
      }
      const result = await createGame(services.db, services.user.uid, {
        gameName: String(data.get('gameName') ?? ''),
        displayName: String(data.get('displayName') ?? ''),
        startingStack,
      })
      onJoined({ code: result.code, view: 'table', recoveryCode: result.recoveryCode })
    } catch (reason) {
      if (hostRoute) disablePartyAudio()
      setError(reason)
    } finally {
      setPending(false)
    }
  }

  const submitRecovery = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setPending(true)
    setError(null)
    const data = new FormData(event.currentTarget)
    const code = normalizeRoomCode(String(data.get('code') ?? ''))
    const recoveryCode = normalizeRecoveryCode(String(data.get('recoveryCode') ?? ''))
    try {
      // Recovery is a deliberate user gesture, so it is also our opportunity to
      // unlock the lobby soundtrack on browsers that gate Web Audio playback.
      try { await enablePartyAudio('lobby') } catch { /* Recovery still works muted. */ }
      await recoverTableAuthority(services.db, services.user.uid, {
        code,
        recoveryCode,
        displayName: String(data.get('displayName') ?? ''),
      })
      history.replaceState(null, '', '/host')
      onJoined({ code, view: 'table', recoveryCode: recoveryCode.toUpperCase() })
    } catch (reason) {
      disablePartyAudio()
      setError(reason)
    } finally {
      setPending(false)
    }
  }

  return (
    <main className="welcome-shell">
      <div className="welcome-glow" />
      <header className="welcome-header"><Logo /><LanguageToggle /></header>
      <section className="welcome-copy">
        <span className="eyebrow">{hostRoute ? text('TABLE SCREEN', 'מסך שולחן') : text('GAME NIGHT', 'ערב משחק')}</span>
        <h1>{hostRoute ? text('HOST THE', 'מארחים את') : text('YOUR POT', 'הקופה שלכם')}<br/><em>{hostRoute ? text('ROOM', 'החדר') : text('LIVE', 'בשידור חי')}</em></h1>
        <p>{hostRoute ? text('One tap opens a fresh musical lobby.', 'נגיעה אחת פותחת לובי מוזיקלי חדש.') : text('Chips, pot & actions—live on every device.', 'צ׳יפים, קופה ופעולות — בכל המכשירים.')}</p>
        <div className="feature-row"><span>● {text('LIVE SYNC', 'סנכרון חי')}</span><span>● {text('NO CARDS', 'בלי קלפים')}</span><span>● {text('NO SIGN-UP', 'בלי הרשמה')}</span></div>
      </section>
      <section className="entry-card">
        {hostRoute ? <div className="host-launch-label"><span>●</span>{text('FRESH LOBBY', 'לובי חדש')}</div> : <div className="segmented segmented--wide">
          <button className={tab === 'join' ? 'active' : ''} onClick={() => { setTab('join'); setError(null) }}>{text('JOIN', 'הצטרפות')}</button>
          <button onClick={() => location.assign('/host')}>{text('HOST', 'אירוח')}</button>
          {ENABLE_TABLE_RECOVERY && <button className={tab === 'recover' ? 'active' : ''} onClick={() => { setTab('recover'); setError(null) }}>{text('RECOVER', 'שחזור')}</button>}
        </div>}
        {tab === 'join' ? (
          <form onSubmit={submitJoin} className="form-stack" noValidate>
            {queryCode ? <div className="deep-link-code"><span>{text('JOIN TABLE', 'כניסה לשולחן')}</span><b dir="ltr">{queryCode}</b><input type="hidden" name="code" value={queryCode}/></div> : <label><span>{text('TABLE CODE', 'קוד שולחן')}</span><input className="code-input" name="code" placeholder="ABCD" aria-label={text('Table code', 'קוד שולחן')} maxLength={4} autoCapitalize="characters" required /></label>}
            <label><span>{text('YOUR NAME', 'השם שלך')}</span><input name="displayName" placeholder={text('Name on table', 'שם בשולחן')} aria-label={text('Your display name', 'שם התצוגה שלך')} maxLength={30} autoComplete="nickname" required /></label>
            <p className="form-hint"><Icon name="phone"/>{text('Your chip wallet', 'ארנק הצ׳יפים שלך')}</p>
            {error != null && <p className="form-error" role="alert">{readableFirebaseError(error)}</p>}
            <button className="button button--primary button--large" disabled={pending}>{pending ? text('JOINING…', 'מצטרפים…') : text('JOIN TABLE', 'כניסה לשולחן')}</button>
          </form>
        ) : tab === 'create' ? (
          <form onSubmit={submitCreate} className="form-stack" noValidate>
            <label><span>{text('GAME NAME', 'שם המשחק')}</span><input name="gameName" defaultValue={text('Poker Night', 'ערב פוקר')} placeholder={text('Thursday game', 'משחק חמישי')} aria-label={text('Game name', 'שם המשחק')} maxLength={40} required /></label>
            <label><span>{text('TABLE DEVICE', 'מכשיר שולחן')}</span><input name="displayName" defaultValue={text('Table', 'שולחן')} placeholder={text('Living-room iPad', 'אייפד סלון')} aria-label={text('Table device name', 'שם מכשיר השולחן')} maxLength={30} required /></label>
            <label><span>{text('STARTING CHIPS', 'צ׳יפים התחלתיים')}</span><input name="startingStack" type="number" inputMode="numeric" defaultValue={400} min={100} max={1000000} step={1} aria-label={text('Starting chips per player', 'צ׳יפים התחלתיים לשחקן')} required /><small className="field-note">{text('DEFAULT', 'ברירת מחדל')}: 10×1, 8×5, 10×10, 6×25, 1×100</small></label>
            <p className="form-hint"><Icon name="table"/>{text('Winner, next hand & fixes', 'זוכה, יד חדשה ותיקונים')}</p>
            {error != null && <p className="form-error" role="alert">{readableFirebaseError(error)}</p>}
            <button className="button button--primary button--large host-launch-button" disabled={pending}>{pending ? text('OPENING THE ROOM…', 'פותחים את החדר…') : text('HOST A GAME', 'אירוח משחק')}</button>
            {resumableTable && <><div className="resume-divider"><span>{text('OR', 'או')}</span></div><button type="button" className="button button--ghost resume-room-button" disabled={pending} onClick={resumeTable}>{text(`RESUME ROOM ${resumableTable.code}`, `חזרה לחדר ${resumableTable.code}`)}</button></>}
          </form>
        ) : (
          <form onSubmit={submitRecovery} className="form-stack" noValidate>
            <label><span>{text('TABLE CODE', 'קוד שולחן')}</span><input className="code-input" name="code" placeholder="ABCD" aria-label={text('Table code', 'קוד שולחן')} maxLength={4} autoCapitalize="characters" required /></label>
            <label><span>{text('RECOVERY CODE', 'קוד שחזור')}</span><input className="recovery-input" name="recoveryCode" placeholder="XXXX-XXXX-XXXX" aria-label={text('Recovery code', 'קוד שחזור')} maxLength={14} autoCapitalize="characters" dir="ltr" required /></label>
            <label><span>{text('TABLE DEVICE', 'מכשיר שולחן')}</span><input name="displayName" defaultValue={text('Recovered table', 'שולחן משוחזר')} aria-label={text('Recovered table device name', 'שם מכשיר השולחן המשוחזר')} maxLength={30} required /></label>
            <p className="form-hint"><Icon name="table"/>{text('Replaces control; pot and log stay', 'מחליף שליטה; הקופה והיומן נשמרים')}</p>
            {error != null && <p className="form-error" role="alert">{readableFirebaseError(error)}</p>}
            <button className="button button--primary button--large" disabled={pending}>{pending ? text('RECOVERING…', 'משחזרים…') : text('RECOVER TABLE', 'שחזור שולחן')}</button>
          </form>
        )}
      </section>
      <footer className="welcome-footer">Poker Pot Live · {text('Play easy', 'משחקים בכיף')}</footer>
    </main>
  )
}

function GameRoom({ services, session, onSessionChange }: { services: FirebaseServices; session: GameSession; onSessionChange: (session: GameSession | null) => void }) {
  const live = useLiveGame(services, session.code)
  const [notice, setNotice] = useState<Notice | null>(null)
  const [online, setOnline] = useState(() => navigator.onLine)
  const noticeTimer = useRef<number | null>(null)
  const operationLock = useRef(false)
  const recoverySetupStarted = useRef(false)
  const seenAudioActions = useRef(new Set<string>())
  const audioBaselineReady = useRef(false)
  const roomMountedAt = useRef(Date.now())
  const momentTimer = useRef<number | null>(null)
  const [moment, setMoment] = useState<PartyMoment | null>(null)
  const [musicOn, setMusicOn] = useState(() => isPartyMusicEnabled())
  const [sfxOn, setSfxOn] = useState(() => isPartySfxEnabled())
  const [menuOpen, setMenuOpen] = useState(false)
  const tableAudioAuthority = live.game?.tableUid === services.user.uid
  const audioScene = live.game ? (gamePhase(live.game) === 'lobby' ? 'lobby' : 'game') : 'silent'

  const showNotice = (message: Notice) => {
    setNotice(message)
    if (noticeTimer.current) window.clearTimeout(noticeTimer.current)
    noticeTimer.current = window.setTimeout(() => setNotice(null), 2600)
  }
  const copyRoom = async () => {
    const url = new URL(location.origin)
    url.searchParams.set('room', session.code)
    await navigator.clipboard.writeText(url.toString())
    showNotice(uiMessage('Join link copied', 'קישור ההצטרפות הועתק'))
  }

  useEffect(() => {
    const markOnline = () => setOnline(true)
    const markOffline = () => setOnline(false)
    window.addEventListener('online', markOnline)
    window.addEventListener('offline', markOffline)
    return () => {
      window.removeEventListener('online', markOnline)
      window.removeEventListener('offline', markOffline)
    }
  }, [])

  useEffect(() => {
    if (!tableAudioAuthority || live.loading || live.actionsFromCache) return
    if (!audioBaselineReady.current) {
      live.actions.forEach((action) => seenAudioActions.current.add(action.id))
      audioBaselineReady.current = true
      return
    }
    const fresh = live.actions.filter((action) => !seenAudioActions.current.has(action.id))
    fresh.forEach((action) => {
      const actionTime = action.createdAt?.toMillis()
      if (!actionTime) return
      seenAudioActions.current.add(action.id)
      if (document.hidden || actionTime < roomMountedAt.current - 1_000 || Date.now() - actionTime > 10_000) return
      if (action.type === 'join') playPartySound('join')
      if (action.type === 'start_game') playPartySound('start')
      if (action.type === 'set_leader') playPartySound('leader')
      if (action.type === 'restart_game') playPartySound('hand')
      if (action.type === 'new_round') playPartySound('round')
      if (action.type === 'bet') playPartySound('chips', action.amount)
      if (action.type === 'fold') playPartySound('fold')
      if (action.type === 'award') playPartySound('winner')
      if (action.type === 'new_hand' || action.type === 'reset_hand') playPartySound('hand')
      const nextMoment = partyMomentForAction(action)
      if (nextMoment) {
        setMoment(nextMoment)
        if (momentTimer.current) window.clearTimeout(momentTimer.current)
        momentTimer.current = window.setTimeout(() => setMoment(null), action.type === 'start_game' ? 2_800 : action.type === 'set_leader' ? 1_650 : 2_300)
      }
    })
  }, [live.actions, live.actionsFromCache, live.loading, tableAudioAuthority])

  useEffect(() => {
    if (!tableAudioAuthority || audioScene === 'silent') return
    setPartyAudioScene(audioScene)
    const audioState = getPartyAudioState()
    setMusicOn(audioState.musicEnabled)
    setSfxOn(audioState.sfxEnabled)
  }, [audioScene, tableAudioAuthority])

  useEffect(() => {
    if (audioScene === 'lobby') setMenuOpen(false)
  }, [audioScene])

  useEffect(() => {
    if (live.game && !tableAudioAuthority && isPartyAudioEnabled()) {
      disablePartyAudio()
      setMusicOn(false)
      setSfxOn(false)
    }
  }, [live.game, tableAudioAuthority])

  useEffect(() => {
    if (!live.loading && (live.error || !live.game || !live.member)) {
      disablePartyAudio()
      setMusicOn(false)
      setSfxOn(false)
    }
  }, [live.error, live.game, live.loading, live.member])

  useEffect(() => () => {
    if (momentTimer.current) window.clearTimeout(momentTimer.current)
  }, [])

  useEffect(() => {
    if (!tableAudioAuthority) return
    const handleVisibility = () => {
      if (document.hidden) suspendPartyAudio()
      else void restorePartyAudio().then(() => {
        const audioState = getPartyAudioState()
        setMusicOn(audioState.musicEnabled)
        setSfxOn(audioState.sfxEnabled)
      })
    }
    const handlePageHide = () => suspendPartyAudio()
    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('pagehide', handlePageHide)
    window.addEventListener('pageshow', handleVisibility)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('pagehide', handlePageHide)
      window.removeEventListener('pageshow', handleVisibility)
    }
  }, [tableAudioAuthority])

  useEffect(() => {
    if (!ENABLE_TABLE_RECOVERY) return
    if (!live.game || live.game.tableUid !== services.user.uid || session.recoveryCode || recoverySetupStarted.current) return
    recoverySetupStarted.current = true
    rotateRecoveryCode(services.db, session.code, services.user.uid)
      .then((recoveryCode) => {
        onSessionChange({ ...session, recoveryCode })
        showNotice(uiMessage('New table recovery code created', 'נוצר קוד שחזור חדש לשולחן'))
      })
      .catch((reason) => {
        recoverySetupStarted.current = false
        showNotice({ error: reason })
      })
  }, [live.game, onSessionChange, services, session])

  if (live.loading) return <main className="splash"><Logo /><LanguageToggle/><Spinner label={text('SETTING CHIPS…', 'מסדרים צ׳יפים…')} /></main>
  if (live.error || !live.game || !live.member) {
    return <FatalState message={live.error ?? text('Room unavailable for this account', 'החדר אינו זמין לחשבון הזה')} roomCode={session.code} onLeaveRoom={() => onSessionChange(null)} />
  }

  const currentPlayer = live.players.find((player) => player.uid === services.user.uid) ?? null
  const isTableAuthority = live.game.tableUid === services.user.uid
  const activeView = isTableAuthority ? 'table' : (live.member.isPlayer && currentPlayer ? 'player' : 'table')
  const run = async (operation: () => Promise<void>, success: UiMessage): Promise<boolean> => {
    if (operationLock.current) return false
    operationLock.current = true
    try {
      await operation()
      showNotice(success)
      return true
    } catch (reason) {
      showNotice({ error: reason })
      return false
    } finally {
      operationLock.current = false
    }
  }
  const toggleMusic = async () => {
    try {
      setMusicOn(await setPartyMusicEnabled(!musicOn))
    } catch {
      showNotice(uiMessage('Music needs another tap', 'המוזיקה צריכה נגיעה נוספת'))
    }
  }
  const toggleSfx = async () => {
    try {
      setSfxOn(await setPartySfxEnabled(!sfxOn))
    } catch {
      showNotice(uiMessage('Sound effects need another tap', 'האפקטים צריכים נגיעה נוספת'))
    }
  }

  if (gamePhase(live.game) === 'lobby') {
    return (
      <main className={`party-lobby party-lobby--${activeView}`}>
        {activeView === 'player' && currentPlayer ? (
          <PlayerLobby
            services={services}
            game={live.game}
            player={currentPlayer}
            players={live.players}
            run={run}
          />
        ) : (
          <TableLobby
            services={services}
            game={live.game}
            players={live.players}
            isTableAuthority={isTableAuthority}
            run={run}
            musicOn={musicOn}
            sfxOn={sfxOn}
            onMusicToggle={toggleMusic}
            onSfxToggle={toggleSfx}
          />
        )}
        <button className="leave-link leave-link--lobby" onClick={() => onSessionChange(null)}>{text('EXIT LOBBY', 'יציאה מהלובי')}</button>
        {moment && <PartyMomentOverlay moment={moment}/>} 
        {notice && <div className="toast" role="status">{'error' in notice ? readableFirebaseError(notice.error) : text(notice.en, notice.he)}</div>}
      </main>
    )
  }

  return (
    <main className={`game-shell game-shell--${activeView}`}>
      <header className="game-header">
        <Logo compact />
        <button className="room-code" aria-label={text(`Copy table code ${session.code}`, `העתקת קוד שולחן ${session.code}`)} onClick={copyRoom}><span>{text('CODE', 'קוד')}</span><b dir="ltr">{session.code}</b><Icon name="copy"/></button>
        {(!online || live.fromCache) && <div className="live-badge live-badge--offline" role="status"><i/> {!online ? text('OFFLINE', 'לא מחובר') : text('CACHE', 'מטמון')}</div>}
        <button className="game-menu-button" aria-label={activeView === 'table' ? text('Table menu', 'תפריט שולחן') : text('More options', 'אפשרויות נוספות')} aria-expanded={menuOpen} aria-controls="game-overflow-panel" onClick={() => setMenuOpen(true)}><Icon name="more"/></button>
      </header>
      {activeView === 'player' && currentPlayer ? (
        <PlayerView services={services} game={live.game} member={live.member} player={currentPlayer} players={live.players} actions={live.actions} isTableAuthority={isTableAuthority} run={run} menuOpen={menuOpen} onMenuClose={() => setMenuOpen(false)} onExit={() => onSessionChange(null)} />
      ) : (
        <TableView
          services={services}
          game={live.game}
          member={live.member}
          players={live.players}
          actions={live.actions}
          isTableAuthority={isTableAuthority}
          run={run}
          recoveryCode={session.recoveryCode}
          onRecoveryCode={(recoveryCode) => onSessionChange({ ...session, recoveryCode })}
          onStartOver={(next) => onSessionChange(next)}
          menuOpen={menuOpen}
          onMenuClose={() => setMenuOpen(false)}
          onExit={() => onSessionChange(null)}
          musicOn={musicOn}
          sfxOn={sfxOn}
          onMusicToggle={toggleMusic}
          onSfxToggle={toggleSfx}
        />
      )}
      {moment && <PartyMomentOverlay moment={moment}/>} 
      {notice && <div className="toast" role="status">{'error' in notice ? readableFirebaseError(notice.error) : text(notice.en, notice.he)}</div>}
    </main>
  )
}

function LeaderAssignmentControl({
  services,
  game,
  players,
  run,
  party = false,
}: Pick<ViewProps, 'services' | 'game' | 'players' | 'run'> & { party?: boolean }) {
  const currentLeader = players.find((player) => player.uid === game.leaderUid) ?? null
  const [open, setOpen] = useState(false)
  const [targetUid, setTargetUid] = useState(game.leaderUid ?? players[0]?.uid ?? '')
  const [pending, setPending] = useState(false)
  const [failed, setFailed] = useState(false)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const fallbackUid = currentLeader?.uid ?? players[0]?.uid ?? ''
  const selectedUid = players.some((player) => player.uid === targetUid) ? targetUid : fallbackUid
  const selected = players.find((player) => player.uid === selectedUid) ?? null
  const canChange = players.length > 0 && (!currentLeader || players.some((player) => player.uid !== currentLeader.uid))

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open) {
      setTargetUid(currentLeader?.uid ?? players[0]?.uid ?? '')
      setFailed(false)
      if (!dialog.open) dialog.showModal()
    } else if (dialog.open) {
      dialog.close()
    }
  }, [open])

  const assign = async () => {
    if (!selected || selected.uid === game.leaderUid) return
    setFailed(false)
    setPending(true)
    const succeeded = await run(
      () => setLeader(services.db, game.code, services.user.uid, selected.uid, game.leaderUid ?? ''),
      uiMessage(`${selected.name} is now the Leader`, `${selected.name} מוביל/ה עכשיו`),
    )
    setPending(false)
    if (succeeded) setOpen(false)
    else setFailed(true)
  }

  return <>
    <div className={`leader-assignment-bar ${party ? 'leader-assignment-bar--party' : ''}`} role="status" aria-live="polite">
      <span className="leader-assignment-crown">♛</span>
      <div><small>{text('LEADER', 'מוביל/ה')}</small><b>{currentLeader?.name ?? text('NOT ASSIGNED', 'לא נבחר/ה')}</b></div>
      <button disabled={!canChange} onClick={() => setOpen(true)}>{text(currentLeader ? 'CHANGE' : 'ASSIGN', currentLeader ? 'החלפה' : 'בחירה')}</button>
    </div>
    <dialog ref={dialogRef} className="confirm-dialog leader-dialog" aria-labelledby="leader-dialog-title" onCancel={(event) => { event.preventDefault(); if (!pending) setOpen(false) }}>
      <h2 id="leader-dialog-title">{text('CHOOSE THE LEADER', 'בחירת מוביל/ה')}</h2>
      <p>{text('The Leader gets Start, New Round, Award Pot, New Hand, and Restart controls on their phone.', 'המוביל/ה מקבל/ת בטלפון שליטה על התחלה, סבב חדש, הענקת קופה, יד חדשה והתחלה מחדש.')}</p>
      <div className="leader-choice-list" role="radiogroup" aria-label={text('Choose the new Leader', 'בחירת המוביל/ה החדש/ה')}>
        {players.map((player) => <label className={player.uid === selectedUid ? 'selected' : ''} key={player.uid}>
          <input type="radio" name="leader-target" value={player.uid} checked={player.uid === selectedUid} onChange={() => setTargetUid(player.uid)}/>
          <span className="avatar avatar--small">{initials(player.name)}</span>
          <b>{player.name}</b>
          {player.uid === game.leaderUid && <small>{text('CURRENT', 'נוכחי/ת')}</small>}
        </label>)}
      </div>
      <p className="confirm-details">{selected && selected.uid !== game.leaderUid
        ? text(`${selected.name}'s phone will gain control immediately${currentLeader ? `; ${currentLeader.name}'s will lose it` : ''}.`, `הטלפון של ${selected.name} יקבל שליטה מיד${currentLeader ? `; השליטה של ${currentLeader.name} תוסר` : ''}.`)
        : text('Choose a different player to move the crown.', 'בחרו שחקן אחר כדי להעביר את הכתר.')}</p>
      {failed && <p className="dialog-error" role="alert">{text('Could not change the Leader. Check the connection and try again.', 'לא הצלחנו להחליף מוביל/ה. בדקו את החיבור ונסו שוב.')}</p>}
      <div className="leader-dialog-actions"><button className="button button--ghost" disabled={pending} onClick={() => setOpen(false)}>{text('CANCEL', 'ביטול')}</button><button className="button button--primary" disabled={!selected || selected.uid === game.leaderUid || pending} onClick={() => void assign()}>{pending ? text('CHANGING…', 'מחליפים…') : text('MAKE LEADER', 'הפיכה למוביל/ה')}</button></div>
    </dialog>
  </>
}

function TableLobby({
  services,
  game,
  players,
  isTableAuthority,
  run,
  musicOn,
  sfxOn,
  onMusicToggle,
  onSfxToggle,
}: {
  services: FirebaseServices
  game: Game
  players: Player[]
  isTableAuthority: boolean
  run: (operation: () => Promise<void>, success: UiMessage) => Promise<boolean>
  musicOn: boolean
  sfxOn: boolean
  onMusicToggle: () => void
  onSfxToggle: () => void
}) {
  const joinUrl = new URL('/', location.origin)
  joinUrl.searchParams.set('room', game.code)
  const leader = players.find((player) => player.uid === game.leaderUid)
  return (
    <div className="table-lobby-stage">
      <p className="sr-only" aria-live="polite">{leader ? text(`${players.length} players joined. ${leader.name} is Leader.`, `${players.length} שחקנים הצטרפו. ${leader.name} מוביל/ה.`) : text('Waiting for the first player.', 'ממתינים לשחקן הראשון.')}</p>
      <div className="party-shape party-shape--one"/><div className="party-shape party-shape--two"/><div className="party-shape party-shape--three"/>
      <header className="lobby-brand-row"><Logo compact/><span className="lobby-live-pill">● {text('LOBBY LIVE', 'לובי חי')}</span><AudioControls compact musicOn={musicOn} sfxOn={sfxOn} onMusicToggle={onMusicToggle} onSfxToggle={onSfxToggle}/><LanguageToggle/></header>
      <section className="lobby-hero">
        <div className="lobby-join-card">
          <span className="lobby-kicker">{text('JOIN THE POT', 'מצטרפים לקופה')}</span>
          <div className="lobby-code" dir="ltr">{game.code}</div>
          <div className="lobby-qr"><QRCodeSVG value={joinUrl.toString()} size={224} bgColor="#fffdf4" fgColor="#24133d" level="M" aria-label={text(`Join room ${game.code} QR code`, `קוד QR להצטרפות לחדר ${game.code}`)} /></div>
          <p>{text('Scan with your phone—or visit poker.eladkatz.us', 'סרקו בטלפון — או היכנסו ל־poker.eladkatz.us')}</p>
        </div>
        <div className="lobby-roster-panel">
          <div className="lobby-title"><span>{game.name}</span><h1>{players.length ? text('WHO’S IN?', 'מי בפנים?') : text('CALL THE CREW!', 'קוראים לחבר׳ה!')}</h1><p>{leader ? text(`${leader.name} is the Leader`, `${leader.name} מוביל/ה`) : text('The first player becomes Leader', 'השחקן הראשון הופך למוביל')}</p></div>
          <div className="lobby-player-grid">
            {players.map((player, index) => <article className="lobby-player-card" style={{ '--player-index': index } as CSSProperties} key={player.uid}><span className="chip-character"><i>{initials(player.name)}</i></span><b>{player.name}</b>{player.uid === game.leaderUid && <em>♛ {text('LEADER', 'מוביל/ה')}</em>}</article>)}
            {players.length === 0 && <div className="lobby-empty"><span>↗</span><b>{text('SCAN TO JOIN', 'סרקו להצטרפות')}</b><small>{text('Names will pop up here', 'השמות יקפצו כאן')}</small></div>}
          </div>
          {isTableAuthority && <LeaderAssignmentControl services={services} game={game} players={players} run={run} party/>}
          <div className="lobby-waiting"><span className="waiting-dots"><i/><i/><i/></span><div><b>{players.length < 2 ? text('WAITING FOR PLAYERS', 'ממתינים לשחקנים') : text('WAITING FOR THE LEADER', 'ממתינים למוביל/ה')}</b><small>{players.length < 2 ? text('At least two people make a game', 'צריך לפחות שני אנשים') : text('Leader taps EVERYONE’S IN on their phone', 'המוביל/ה לוחצ/ת כולם בפנים בטלפון')}</small></div></div>
          {isTableAuthority && players.length >= 2 && <button className="table-start-fallback" onClick={() => run(() => startGame(services.db, game.code, services.user.uid), uiMessage('Game started', 'המשחק התחיל'))}>{text('START FROM TABLE', 'התחלה מהשולחן')}</button>}
        </div>
      </section>
    </div>
  )
}

function PlayerLobby({
  services,
  game,
  player,
  players,
  run,
}: {
  services: FirebaseServices
  game: Game
  player: Player
  players: Player[]
  run: (operation: () => Promise<void>, success: UiMessage) => Promise<boolean>
}) {
  const isLeader = game.leaderUid === player.uid
  return (
    <div className="player-lobby-card">
      <header><Logo compact/><LanguageToggle/></header>
      <span className="player-lobby-room">{text('ROOM', 'חדר')} <b dir="ltr">{game.code}</b></span>
      <div className={`player-chip-hero ${isLeader ? 'player-chip-hero--leader' : ''}`}><span>{initials(player.name)}</span>{isLeader && <i>♛</i>}</div>
      <p className="player-lobby-kicker">{isLeader ? text('YOU’RE THE LEADER!', 'את/ה המוביל/ה!') : text('YOU’RE IN!', 'את/ה בפנים!')}</p>
      <h1>{isLeader ? text('Round up the room.', 'אוספים את כולם.') : text('Look at the table.', 'מסתכלים על השולחן.')}</h1>
      <p>{isLeader ? text('When every name is on the table, start the game for everyone.', 'כשכל השמות מופיעים על השולחן, מתחילים לכולם.') : text('The Leader will start when everybody is ready.', 'המוביל/ה יתחיל/תתחיל כשכולם מוכנים.')}</p>
      <div className="player-lobby-count"><span>{players.length}</span>{text(players.length === 1 ? 'PLAYER IN' : 'PLAYERS IN', players.length === 1 ? 'שחקן בפנים' : 'שחקנים בפנים')}</div>
      {isLeader ? <button className="everybody-button" disabled={players.length < 2} onClick={() => run(() => startGame(services.db, game.code, services.user.uid), uiMessage('Everybody is in!', 'כולם בפנים!'))}><span>{text('EVERYONE’S IN', 'כולם בפנים')}</span><small>{players.length < 2 ? text('WAIT FOR ONE MORE', 'ממתינים לעוד שחקן') : text('START FOR EVERYONE', 'מתחילים לכולם')}</small></button> : <div className="look-up-prompt"><span>↑</span>{text('KEEP THIS SCREEN OPEN', 'משאירים את המסך פתוח')}</div>}
    </div>
  )
}

interface ViewProps {
  services: FirebaseServices
  game: Game
  member: Member
  players: Player[]
  actions: GameAction[]
  isTableAuthority: boolean
  run: (operation: () => Promise<void>, success: UiMessage) => Promise<boolean>
}

function LeaderControls({ services, game, players, run, externalBusy = false }: Pick<ViewProps, 'services' | 'game' | 'players' | 'run'> & { externalBusy?: boolean }) {
  const [targetUid, setTargetUid] = useState(players[0]?.uid ?? '')
  const [restartOpen, setRestartOpen] = useState(false)
  const [restartPending, setRestartPending] = useState(false)
  const [actionPending, setActionPending] = useState<'round' | 'award' | 'hand' | null>(null)
  const busy = externalBusy || actionPending !== null || restartPending
  const selectedUid = players.some((candidate) => candidate.uid === targetUid) ? targetUid : (players[0]?.uid ?? '')
  const selected = players.find((candidate) => candidate.uid === selectedUid)
  const nextRound = async () => {
    if (busy) return
    setActionPending('round')
    try {
      await run(
        () => startNextBettingRound(services.db, game.code, services.user.uid, bettingRound(game)),
        uiMessage(`Betting round ${bettingRound(game) + 1} started`, `סבב הימורים ${bettingRound(game) + 1} התחיל`),
      )
    } finally {
      setActionPending(null)
    }
  }
  const award = async () => {
    if (!selected || busy) return
    setActionPending('award')
    try {
      await run(
        () => awardPot(services.db, game.code, services.user.uid, selected.uid),
        uiMessage(`Pot awarded to ${selected.name}`, `הקופה הוענקה ל${selected.name}`),
      )
    } finally {
      setActionPending(null)
    }
  }
  const nextHand = async () => {
    if (busy) return
    setActionPending('hand')
    try {
      await run(
        () => startNewHand(services.db, game.code, services.user.uid, game.handNumber),
        uiMessage('New hand started', 'יד חדשה התחילה'),
      )
    } finally {
      setActionPending(null)
    }
  }
  const restart = async (confirmedCode: string) => {
    setRestartPending(true)
    const succeeded = await run(
      () => restartGame(services.db, game.code, services.user.uid, confirmedCode),
      uiMessage('Everyone returned to the lobby', 'כולם חזרו ללובי'),
    )
    setRestartPending(false)
    if (succeeded) setRestartOpen(false)
    return succeeded
  }

  return <section className="leader-control-card" aria-busy={busy}>
    <header><span>♛</span><div><b>{text('LEADER CONTROLS', 'בקרת מוביל/ה')}</b><small>{text('You move the room forward', 'את/ה מקדם/ת את החדר')}</small></div></header>
    <div className="leader-control-grid">
      <button disabled={game.handOpen === false || busy} onClick={() => void nextRound()}>◉ <span>{actionPending === 'round' ? text('STARTING…', 'מתחילים…') : text('NEW ROUND', 'סבב חדש')}</span></button>
      <label><small>{text('POT WINNER', 'זוכה בקופה')}</small><select aria-label={text('Choose pot winner', 'בחירת זוכה בקופה')} value={selectedUid} disabled={busy} onChange={(event) => setTargetUid(event.target.value)}>{players.map((candidate) => <option key={candidate.uid} value={candidate.uid}>{candidate.name}</option>)}</select></label>
      <button className="leader-control-award" disabled={!selected || game.pot === 0 || game.handOpen === false || busy} onClick={() => void award()}>🏆 <span>{actionPending === 'award' ? text('AWARDING…', 'מעניקים…') : text('AWARD POT', 'הענקת קופה')}</span></button>
      <button disabled={game.handOpen !== false || game.pot !== 0 || busy} onClick={() => void nextHand()}>↻ <span>{actionPending === 'hand' ? text('STARTING…', 'מתחילים…') : text('NEW HAND', 'יד חדשה')}</span></button>
      <button className="leader-control-restart" disabled={busy} onClick={() => setRestartOpen(true)}>⟲ <span>{text('RESTART GAME', 'התחלה מחדש')}</span></button>
    </div>
    {restartOpen && <RoomCodeConfirmation code={game.code} title={text('RETURN EVERYONE TO THE LOBBY?', 'להחזיר את כולם ללובי?')} description={text('All balances reset to the starting stack. The same players and Leader stay in the room.', 'כל היתרות יחזרו לכמות ההתחלתית. אותם שחקנים והמוביל/ה יישארו בחדר.')} details={text('The current pot and hand are cleared. The complete action log stays saved.', 'הקופה והיד הנוכחיות יימחקו. יומן הפעולות המלא יישמר.')} confirmLabel={text('RESET & RETURN TO LOBBY', 'איפוס וחזרה ללובי')} pending={restartPending} onCancel={() => setRestartOpen(false)} onConfirm={restart}/>} 
  </section>
}

function AwardWinnerDialog({ open, services, game, players, run, onClose, requiredWinnerUid }: Pick<ViewProps, 'services' | 'game' | 'players' | 'run'> & { open: boolean; onClose: () => void; requiredWinnerUid?: string }) {
  const [targetUid, setTargetUid] = useState(requiredWinnerUid ?? players[0]?.uid ?? '')
  const [pending, setPending] = useState(false)
  const [failed, setFailed] = useState(false)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const selectedUid = players.some((candidate) => candidate.uid === targetUid) ? targetUid : (players[0]?.uid ?? '')
  const selected = players.find((candidate) => candidate.uid === selectedUid) ?? null

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open) {
      if (!dialog.open) {
        setFailed(false)
        setTargetUid(requiredWinnerUid ?? players.find((candidate) => candidate.foldedHand !== game.handNumber)?.uid ?? players[0]?.uid ?? '')
        dialog.showModal()
      }
    } else if (dialog.open) dialog.close()
  }, [open, game.handNumber, players, requiredWinnerUid])

  const award = async () => {
    if (!selected || pending) return
    setFailed(false)
    setPending(true)
    const succeeded = await run(
      () => awardPot(services.db, game.code, services.user.uid, selected.uid),
      uiMessage(`Pot awarded to ${selected.name}`, `הקופה הוענקה ל${selected.name}`),
    )
    setPending(false)
    if (succeeded) onClose()
    else setFailed(true)
  }

  return <dialog ref={dialogRef} className="confirm-dialog winner-dialog" aria-labelledby="winner-dialog-title" onCancel={(event) => { event.preventDefault(); if (!pending) onClose() }}>
    <div className="dialog-title-row"><div><span>{text('HAND COMPLETE', 'היד הסתיימה')}</span><h2 id="winner-dialog-title">{text('Choose pot winner', 'בחירת הזוכה בקופה')}</h2></div><button className="icon-button icon-button--light" aria-label={text('Close winner picker', 'סגירת בחירת זוכה')} disabled={pending} onClick={onClose}><Icon name="close"/></button></div>
    <p>{text(`Award the full pot of ${formatChips(game.pot)}.`, `הענקת כל הקופה בסך ${formatChips(game.pot)}.`)}</p>
    <div className="winner-choice-list" role="radiogroup" aria-label={text('Choose pot winner', 'בחירת זוכה בקופה')}>
      {players.map((candidate) => <label className={candidate.uid === selectedUid ? 'selected' : ''} key={candidate.uid}>
        <input type="radio" name="winner-target" value={candidate.uid} checked={candidate.uid === selectedUid} disabled={pending || Boolean(requiredWinnerUid && requiredWinnerUid !== candidate.uid)} onChange={() => setTargetUid(candidate.uid)}/>
        <span className="avatar avatar--small">{initials(candidate.name)}</span>
        <b>{candidate.name}</b>
        <strong>{formatChips(candidate.balance)}</strong>
      </label>)}
    </div>
    {failed && <p className="dialog-error" role="alert">{text('Could not award the pot. Check the connection and try again.', 'לא הצלחנו להעניק את הקופה. בדקו את החיבור ונסו שוב.')}</p>}
    <div className="leader-dialog-actions"><button className="button button--ghost" disabled={pending} onClick={onClose}>{text('CANCEL', 'ביטול')}</button><button className="button button--gold" disabled={!selected || game.pot === 0 || game.handOpen === false || pending} onClick={() => void award()}>{pending ? text('AWARDING…', 'מעניקים…') : text(`AWARD ${formatChips(game.pot)}`, `הענקת ${formatChips(game.pot)}`)}</button></div>
  </dialog>
}

function LeaderActionDock({ services, game, players, run, externalBusy = false, remoteAction, remoteState }: Pick<ViewProps, 'services' | 'game' | 'players' | 'run'> & { externalBusy?: boolean; remoteAction?: RemoteLeaderAction; remoteState?: RemoteHarnessState | null }) {
  const [winnerOpen, setWinnerOpen] = useState(false)
  const [actionPending, setActionPending] = useState<'round' | 'hand' | null>(null)
  const busy = externalBusy || actionPending !== null
  const nextRound = async () => {
    if (busy) return
    setActionPending('round')
    try {
      await run(
        () => startNextBettingRound(services.db, game.code, services.user.uid, bettingRound(game)),
        uiMessage(`Betting round ${bettingRound(game) + 1} started`, `סבב הימורים ${bettingRound(game) + 1} התחיל`),
      )
    } finally {
      setActionPending(null)
    }
  }
  const nextHand = async () => {
    if (busy) return
    setActionPending('hand')
    try {
      await run(
        () => startNewHand(services.db, game.code, services.user.uid, game.handNumber),
        uiMessage('New hand started', 'יד חדשה התחילה'),
      )
    } finally {
      setActionPending(null)
    }
  }

  if (remoteState && !remoteAction) return null
  const remoteStreet = remoteState?.phase === 'awaiting_street'
    ? remoteState.prompt.headline.replace(/^open\s+/i, '').toUpperCase()
    : 'NEXT ROUND'
  const leaderDetail = remoteAction === 'open-street'
    ? text(`Betting is settled. Open the ${remoteStreet}.`, `ההימורים הסתיימו. פותחים ${remoteStreet}.`)
    : remoteAction === 'award-pot'
      ? text('Award the exact winner shown above.', 'מעניקים לזוכה המדויק/ת שמופיע/ה למעלה.')
      : remoteAction === 'start-next-hand'
        ? text('The hand passed. Start the next one.', 'היד עברה בהצלחה. מתחילים את היד הבאה.')
        : game.handOpen === false
          ? text('The hand is complete. Start the next one.', 'היד הסתיימה. מתחילים את היד הבאה.')
          : text('When betting is settled, start the next round.', 'כשההימורים הסתיימו, מתחילים את הסבב הבא.')

  return <section className="leader-action-dock" aria-label={text('Leader actions', 'פעולות מוביל/ה')} aria-busy={busy}>
    <div className="leader-action-dock__label"><span>♛</span><div><small>{text('YOU’RE LEADER', 'את/ה מוביל/ה')}</small><b>{leaderDetail}</b></div></div>
    <div className="leader-action-dock__actions">
      {remoteAction === 'start-next-hand' || (!remoteState && game.handOpen === false) ? (
        <button className="leader-action-primary" disabled={game.pot !== 0 || busy} onClick={() => void nextHand()}>↻ {actionPending === 'hand' ? text('STARTING…', 'מתחילים…') : text('START NEXT HAND', 'התחלת היד הבאה')}</button>
      ) : remoteAction === 'award-pot' ? (
        <button className="leader-action-primary" disabled={busy || !remoteState?.expectedWinnerUid || game.pot !== remoteState.expectedPot} onClick={() => setWinnerOpen(true)}>🏆 {text(`AWARD ${formatChips(remoteState?.expectedPot ?? game.pot)}`, `הענקת ${formatChips(remoteState?.expectedPot ?? game.pot)}`)}</button>
      ) : remoteAction === 'open-street' ? (
        <button className="leader-action-primary" disabled={busy} onClick={() => void nextRound()}>◉ {actionPending === 'round' ? text('OPENING…', 'פותחים…') : text(`OPEN ${remoteStreet}`, `פתיחת ${remoteStreet}`)}</button>
      ) : <>
          <button disabled={busy} onClick={() => void nextRound()}>◉ {actionPending === 'round' ? text('STARTING…', 'מתחילים…') : text('NEW ROUND', 'סבב חדש')}</button>
          {game.pot > 0 && <button className="leader-action-primary" disabled={busy} onClick={() => setWinnerOpen(true)}>🏆 {text('AWARD POT', 'הענקת קופה')}</button>}
        </>}
    </div>
    <AwardWinnerDialog open={winnerOpen} onClose={() => setWinnerOpen(false)} services={services} game={game} players={players} run={run} requiredWinnerUid={remoteState?.expectedWinnerUid ?? undefined}/>
  </section>
}

function FoldConfirmationDialog({ open, pending, failed, onCancel, onConfirm }: {
  open: boolean
  pending: boolean
  failed: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  return <dialog ref={dialogRef} className="confirm-dialog fold-dialog" aria-labelledby="fold-dialog-title" aria-describedby="fold-dialog-description" aria-busy={pending} onCancel={(event) => { event.preventDefault(); if (!pending) onCancel() }}>
    <span className="fold-dialog__mark" aria-hidden="true">×</span>
    <h2 id="fold-dialog-title">{text('FOLD THIS HAND?', 'לפרוש מהיד הזו?')}</h2>
    <p id="fold-dialog-description">{text('You can’t add more chips until the next hand. Chips already in the pot stay there.', 'לא יהיה אפשר להוסיף צ׳יפים עד ליד הבאה. צ׳יפים שכבר בקופה נשארים שם.')}</p>
    {failed && <p className="dialog-error" role="alert">{text('Could not fold. Check the connection and try again.', 'לא הצלחנו לרשום פרישה. בדקו את החיבור ונסו שוב.')}</p>}
    <div className="leader-dialog-actions">
      <button type="button" className="button button--ghost" autoFocus disabled={pending} onClick={onCancel}>{text('KEEP PLAYING', 'להמשיך לשחק')}</button>
      <button type="button" className="button button--danger" disabled={pending} onClick={onConfirm}>{pending ? text('FOLDING…', 'פורשים…') : text('FOLD HAND', 'פרישה מהיד')}</button>
    </div>
  </dialog>
}

function PlayerView({ services, game, player, players, actions, run, menuOpen, onMenuClose, onExit }: ViewProps & { player: Player; menuOpen: boolean; onMenuClose: () => void; onExit: () => void }) {
  const remote = useRemoteHarness(services.db, game.code, player.uid)
  const [chipSelections, setChipSelections] = useState<number[]>([])
  const [pending, setPending] = useState(false)
  const [foldOpen, setFoldOpen] = useState(false)
  const [foldPending, setFoldPending] = useState(false)
  const [foldFailed, setFoldFailed] = useState(false)
  const submitLock = useRef(false)
  const submissionId = useRef<string | null>(null)
  const folded = player.foldedHand === game.handNumber
  const handOpen = game.handOpen !== false
  const isLeader = game.leaderUid === player.uid
  const denominations = [1, 5, 10, 25, 100]
  const amount = chipSelections.reduce((total, value) => total + value, 0)
  const ownContribution = roundContribution(game, player)
  const highestContribution = Math.max(0, ...players.filter((item) => item.foldedHand !== game.handNumber).map((item) => roundContribution(game, item)))
  const regularCallAmount = Math.max(0, highestContribution - ownContribution)
  const remotePolicy = remote.state ? getRemoteControlPolicy(remote.state, player.uid, isLeader) : null
  const remoteSynchronized = !remote.state || remote.state.processedActionId === game.lastActionId
  const remoteReady = !remote.loading && !remote.error && remoteSynchronized
  const bettingEnabled = !remote.state || Boolean(remotePolicy?.regularBettingEnabled && remoteReady)
  const foldEnabled = !remote.state || Boolean(remotePolicy?.foldEnabled && remoteReady)
  const callAmount = remote.state
    ? (remote.state.actorUid === player.uid
        ? (remote.state.requiredBlind?.playerUid === player.uid ? remote.state.requiredBlind.amount : remote.state.prompt.toCall)
        : 0)
    : regularCallAmount
  const requiredExactAmount = remote.state?.requiredBlind?.playerUid === player.uid ? remote.state.requiredBlind.amount : null
  const namesByUid = Object.fromEntries(players.map((candidate) => [candidate.uid, candidate.name]))
  useEffect(() => {
    setChipSelections([])
    submissionId.current = null
  }, [game.handNumber, game.bettingRound])
  useEffect(() => {
    if (folded || !handOpen) {
      setFoldOpen(false)
      setFoldFailed(false)
    }
  }, [folded, handOpen])
  useEffect(() => {
    if (remote.state && !foldEnabled) {
      setFoldOpen(false)
      setFoldFailed(false)
    }
  }, [foldEnabled, remote.state])
  useEffect(() => {
    if (!remote.state || !remoteReady || remote.state.actorUid !== player.uid) return
    const suggestedAmount = remote.state.requiredBlind?.playerUid === player.uid
      ? remote.state.requiredBlind.amount
      : remote.state.prompt.toCall
    setChipSelections(suggestedAmount > 0 ? [suggestedAmount] : [])
    submissionId.current = null
  }, [player.uid, remote.state?.actorUid, remote.state?.turnToken, remote.state?.requiredBlind?.playerUid, remote.state?.requiredBlind?.amount, remote.state?.prompt.toCall, remoteReady])
  const chooseAmount = (value: number) => {
    submissionId.current = null
    setChipSelections(value > 0 ? [value] : [])
  }
  const addAmount = (value: number) => {
    submissionId.current = null
    setChipSelections((current) => [...current, value])
  }
  const removeLastAmount = () => {
    submissionId.current = null
    setChipSelections((current) => current.slice(0, -1))
  }
  const submitBet = async () => {
    const parsed = parseChipAmount(amount)
    if (!parsed || submitLock.current || !handOpen || !bettingEnabled || (requiredExactAmount != null && parsed !== requiredExactAmount)) return
    submitLock.current = true
    submissionId.current ??= crypto.randomUUID()
    setPending(true)
    try {
      const succeeded = await run(
        () => contributeChips(services.db, game.code, services.user.uid, parsed, submissionId.current!),
        uiMessage(`${formatChips(parsed)} added to pot`, `${formatChips(parsed)} נוספו לקופה`),
      )
      if (succeeded) {
        setChipSelections([])
        submissionId.current = null
      }
    } finally {
      submitLock.current = false
      setPending(false)
    }
  }
  const confirmFold = async () => {
    if (foldPending || !foldEnabled) return
    setFoldFailed(false)
    setFoldPending(true)
    const succeeded = await run(
      () => foldHand(services.db, game.code, services.user.uid),
      uiMessage('Fold recorded', 'הכיפול נרשם'),
    )
    setFoldPending(false)
    if (succeeded) setFoldOpen(false)
    else setFoldFailed(true)
  }

  return (
    <div className={`player-layout player-layout--focused ${isLeader ? 'player-layout--leader' : ''}`}>
      <section className="player-main player-main--focused">
        <div className="round-meta round-meta--focused"><span>{text(`HAND ${game.handNumber} · ROUND ${bettingRound(game)}`, `יד ${game.handNumber} · סבב ${bettingRound(game)}`)}</span>{isLeader && <b>♛ {text('LEADER', 'מוביל/ה')}</b>}</div>
        <div className="player-score-strip">
          <div><small>{text('YOUR CHIPS', 'הצ׳יפים שלך')}</small><strong>{formatChips(player.balance)}</strong></div>
          <div><small>{text('POT', 'קופה')}</small><strong>{formatChips(game.pot)}</strong></div>
        </div>
        {remote.state && <RemoteTestPanel state={remote.state} holeCards={remote.holeCards} playerUid={player.uid} isLeader={isLeader} namesByUid={namesByUid} disabled={!remoteReady || pending || foldPending} onCheck={remote.submitCheck}/>} 
        {remote.error && <p className="form-error" role="alert">{text('The private test dealer lost its connection. Keep this screen open and check the dealer tab.', 'החיבור לדילר הבדיקה הפרטי נותק. משאירים את המסך פתוח ובודקים את לשונית הדילר.')}</p>}
        {isLeader && (folded || !handOpen) && <LeaderActionDock services={services} game={game} players={players} run={run} externalBusy={pending || foldPending || Boolean(remote.state && !remoteReady)} remoteAction={remotePolicy?.leaderAction} remoteState={remote.state}/>} 
        {!handOpen ? <section className="player-wait-state"><span>✓</span><h1>{text('Hand complete', 'היד הסתיימה')}</h1><p>{folded ? text('The pot was awarded. You’re back in for the next hand.', 'הקופה הוענקה. חוזרים למשחק ביד הבאה.') : text('Waiting for the Leader to start the next hand.', 'ממתינים למוביל/ה להתחלת היד הבאה.')}</p></section>
          : folded ? <section className="player-wait-state player-wait-state--folded"><span>×</span><h1>{text('You folded', 'פרשת מהיד')}</h1><p>{text('Watch the table. You’ll be back in on the next hand.', 'מסתכלים על השולחן. חוזרים ביד הבאה.')}</p></section>
          : <section className="action-panel action-panel--focused">
            <div className="action-panel__heading"><div><span>{text('ADD TO POT', 'הוספה לקופה')}</span><small>{bettingEnabled ? text('Tap chips to build an amount', 'נוגעים בצ׳יפים כדי להרכיב סכום') : text('Wait for your turn', 'ממתינים לתור שלך')}</small></div>{callAmount > 0 && <button aria-label={requiredExactAmount != null ? text(`Select ${callAmount} chips for the blind`, `בחירת ${callAmount} צ׳יפים לבליינד`) : text(`Select ${callAmount} chips to call`, `בחירת ${callAmount} צ׳יפים להשוואה`)} disabled={!bettingEnabled || callAmount > player.balance} onClick={() => chooseAmount(callAmount)}>{callAmount <= player.balance ? text(`${requiredExactAmount != null ? 'POST' : 'TO CALL'} ${formatChips(callAmount)}`, `${requiredExactAmount != null ? 'בליינד' : 'להשוואה'} ${formatChips(callAmount)}`) : text('SHORT STACK', 'אין מספיק')}</button>}</div>
            <div className="chip-composer">{denominations.map((value) => <button aria-label={text(`Add ${value} chip`, `הוספת צ׳יפ ${value}`)} className={`denomination denomination--${value}`} key={value} disabled={!bettingEnabled || pending || amount + value > player.balance} onClick={() => addAmount(value)}><span>{value}</span><small>+{value}</small></button>)}</div>
            <div className="pot-commit-row">
              <button className="chip-backspace" aria-label={text('Remove last chip', 'הסרת הצ׳יפ האחרון')} disabled={!bettingEnabled || amount === 0} onClick={removeLastAmount}>↶</button>
              <button className="pot-commit" aria-label={text(`Add ${formatChips(amount)} chips to pot`, `הוספת ${formatChips(amount)} צ׳יפים לקופה`)} disabled={!bettingEnabled || pending || amount > player.balance || !parseChipAmount(amount) || (requiredExactAmount != null && amount !== requiredExactAmount)} onClick={submitBet}><span>{pending ? text('ADDING…', 'מוסיפים…') : requiredExactAmount != null ? text('POST BLIND', 'הוספת בליינד') : text('ADD TO POT', 'הוספה לקופה')}</span><strong>{formatChips(amount)}</strong></button>
              <button className="chip-clear" aria-label={text('Reset selected amount', 'איפוס הסכום')} disabled={!bettingEnabled || amount === 0} onClick={() => chooseAmount(0)}>{text('CLEAR', 'ניקוי')}</button>
            </div>
            {!remote.state && <p className="check-hint">{text('Checking? Do nothing — the Leader starts the next round.', 'צ׳ק? לא צריך ללחוץ — המוביל/ה מתחיל/ה את הסבב הבא.')}</p>}
            <button className="fold-button fold-button--quiet" disabled={!foldEnabled || pending || foldPending} onClick={() => { setFoldFailed(false); setFoldOpen(true) }}>× {text('FOLD THIS HAND', 'פרישה מהיד')}</button>
          </section>}
        {isLeader && handOpen && !folded && <LeaderActionDock services={services} game={game} players={players} run={run} externalBusy={pending || foldPending || Boolean(remote.state && !remoteReady)} remoteAction={remotePolicy?.leaderAction} remoteState={remote.state}/>} 
      </section>
      <GameOverflowPanel open={menuOpen} panelId="game-overflow-panel" title={text('More options', 'אפשרויות נוספות')} subtitle={`${game.name} · ${game.code}`} onClose={onMenuClose}>
        <OverflowSection title={text('Round details', 'פרטי הסבב')} icon={<Icon name="pot"/>}>
          <div className="round-details"><span>{text('ROUND HIGH', 'גבוה בסבב')}<strong>{formatChips(highestContribution)}</strong></span><span>{text('MY TOTAL', 'הסכום שלי')}<strong>{formatChips(ownContribution)}</strong></span><span>{text('TO CALL', 'להשוואה')}<strong>{formatChips(callAmount)}</strong></span></div>
        </OverflowSection>
        {isLeader && !remote.state && <OverflowSection title={text('Leader controls', 'בקרת מוביל/ה')} icon={<span>♛</span>}><LeaderControls services={services} game={game} players={players} run={run} externalBusy={pending || foldPending}/></OverflowSection>}
        <OverflowSection title={text('Players', 'שחקנים')} icon={<Icon name="users"/>}><PlayersList game={game} players={players}/></OverflowSection>
        <OverflowSection title={text('Activity', 'פעילות')} icon={<Icon name="log"/>}><ActionLog actions={actions}/></OverflowSection>
        <OverflowSection title={text('Game settings', 'הגדרות משחק')} icon={<Icon name="settings"/>}><div className="settings-row"><span>{text('LANGUAGE', 'שפה')}</span><LanguageToggle/></div></OverflowSection>
        <OverflowSection title={text('Leave this view', 'יציאה מהתצוגה')} tone="danger"><button className="button button--danger-outline menu-wide-button" onClick={onExit}>{text('EXIT GAME VIEW', 'יציאה מתצוגת המשחק')}</button></OverflowSection>
      </GameOverflowPanel>
      <FoldConfirmationDialog open={foldOpen} pending={foldPending} failed={foldFailed} onCancel={() => setFoldOpen(false)} onConfirm={() => void confirmFold()}/>
    </div>
  )
}

function TableView({
  services,
  game,
  players,
  actions,
  isTableAuthority,
  run,
  recoveryCode,
  onRecoveryCode,
  onStartOver,
  member,
  menuOpen,
  onMenuClose,
  onExit,
  musicOn,
  sfxOn,
  onMusicToggle,
  onSfxToggle,
}: ViewProps & {
  recoveryCode?: string
  onRecoveryCode: (code: string) => void
  onStartOver: (session: GameSession) => void
  menuOpen: boolean
  onMenuClose: () => void
  onExit: () => void
  musicOn: boolean
  sfxOn: boolean
  onMusicToggle: () => void
  onSfxToggle: () => void
}) {
  const highestContribution = Math.max(0, ...players.filter((player) => player.foldedHand !== game.handNumber).map((player) => roundContribution(game, player)))
  const leader = players.find((player) => player.uid === game.leaderUid)
  return (
    <div className="table-layout table-layout--focused">
      <section className="table-stage table-stage--focused">
        <div className="table-meta table-meta--focused"><span>{game.name}</span><b>{text(`HAND ${game.handNumber} · ROUND ${bettingRound(game)}`, `יד ${game.handNumber} · סבב ${bettingRound(game)}`)}</b></div>
        <div className="felt-table">
          <div className="felt-ring"/>
          <div className="pot-center"><span className="chip-stack"><i/><i/><i/></span><small>{text('POT', 'קופה')}</small><strong>{formatChips(game.pot)}</strong></div>
          <div className="table-players">{players.map((player, index) => <PlayerSeat key={player.uid} player={player} game={game} index={index}/>)}</div>
          <div className="table-context" role="status"><span>{game.handOpen === false ? '✓' : '●'}</span><div><small>{game.handOpen === false ? text('HAND COMPLETE', 'היד הסתיימה') : text(`ROUND ${bettingRound(game)} IN PLAY`, `סבב ${bettingRound(game)} במשחק`)}</small><b>{game.handOpen === false ? text(`Waiting for ${leader?.name ?? 'the Leader'} to start the next hand`, `ממתינים ל${leader?.name ?? 'המוביל/ה'} להתחלת היד הבאה`) : text(`${players.filter((player) => player.foldedHand !== game.handNumber).length} players still in`, `${players.filter((player) => player.foldedHand !== game.handNumber).length} שחקנים עדיין ביד`)}</b></div></div>
        </div>
        {players.length === 0 && <div className="empty-players">{text('SHARE CODE TO JOIN', 'שתפו קוד להצטרפות')}</div>}
      </section>
      <GameOverflowPanel open={menuOpen} panelId="game-overflow-panel" title={text('Table menu', 'תפריט שולחן')} subtitle={`${game.name} · ${game.code}`} onClose={onMenuClose}>
        <OverflowSection title={text('Now playing', 'משחקים עכשיו')} icon={<Icon name="pot"/>}>
          <div className="round-details"><span>{text('HAND', 'יד')}<strong>{game.handNumber}</strong></span><span>{text('ROUND', 'סבב')}<strong>{bettingRound(game)}</strong></span><span>{text('ROUND HIGH', 'גבוה בסבב')}<strong>{formatChips(highestContribution)}</strong></span></div>
        </OverflowSection>
        {isTableAuthority && <OverflowSection title={text('Audio', 'שמע')} icon={<span>♫</span>}><AudioControls musicOn={musicOn} sfxOn={sfxOn} onMusicToggle={onMusicToggle} onSfxToggle={onSfxToggle}/><p className="overflow-help">{text('Lobby music starts on. Gameplay music starts off; sound effects stay on.', 'מוזיקת הלובי מתחילה פעילה. מוזיקת המשחק מתחילה כבויה; האפקטים נשארים פעילים.')}</p></OverflowSection>}
        {isTableAuthority && <OverflowSection title={text('Game controls', 'בקרת משחק')} icon={<Icon name="settings"/>}><TableControls {...{ services, game, players, actions, run }} tableDisplayName={member.displayName} onStartOver={onStartOver} compact /></OverflowSection>}
        <OverflowSection title={text('Players', 'שחקנים')} icon={<Icon name="users"/>}><PlayersList game={game} players={players}/></OverflowSection>
        {ENABLE_TABLE_RECOVERY && isTableAuthority && <OverflowSection title={text('Private recovery', 'שחזור פרטי')} icon={<span>⌁</span>}><RecoveryCard code={recoveryCode} onRotate={async () => {
          let next = ''
          const succeeded = await run(async () => {
            next = await rotateRecoveryCode(services.db, game.code, services.user.uid)
          }, uiMessage('New recovery code created', 'נוצר קוד שחזור חדש'))
          if (succeeded && next) onRecoveryCode(next)
        }} /></OverflowSection>}
        <OverflowSection title={text('Activity', 'פעילות')} icon={<Icon name="log"/>}><ActionLog actions={actions}/></OverflowSection>
        <OverflowSection title={text('Game settings', 'הגדרות משחק')} icon={<Icon name="settings"/>}><div className="settings-row"><span>{text('LANGUAGE', 'שפה')}</span><LanguageToggle/></div></OverflowSection>
        <OverflowSection title={text('Leave this view', 'יציאה מהתצוגה')} tone="danger"><button className="button button--danger-outline menu-wide-button" onClick={onExit}>{text('EXIT TABLE VIEW', 'יציאה מתצוגת השולחן')}</button></OverflowSection>
      </GameOverflowPanel>
    </div>
  )
}

function RecoveryCard({ code, onRotate }: { code?: string; onRotate: () => Promise<void> }) {
  const [revealed, setRevealed] = useState(false)
  const formatted = code ? normalizeRecoveryCode(code).match(/.{1,4}/g)?.join('-') : null
  return <section className="recovery-card"><div><small>{text('TABLE RECOVERY', 'שחזור שולחן')}</small><strong className={!revealed ? 'recovery-secret--hidden' : ''} dir="ltr">{!formatted ? text('CREATING…', 'יוצר…') : revealed ? formatted : '••••-••••-••••'}</strong><span>{text('Keep private—restores this room, pot, balances & log', 'שמרו בפרטיות — משחזר את החדר, הקופה, היתרות והיומן')}</span></div><button className="button button--ghost" aria-pressed={revealed} disabled={!formatted} onClick={() => setRevealed((current) => !current)}>{revealed ? text('HIDE', 'הסתרה') : text('REVEAL', 'חשיפה')}</button><button className="button button--ghost" aria-label={text('Copy recovery code', 'העתקת קוד שחזור')} disabled={!formatted || !revealed} onClick={() => formatted && navigator.clipboard.writeText(formatted)}>{text('COPY', 'העתקה')}</button><button className="button button--ghost" aria-label={text('Create new recovery code', 'יצירת קוד שחזור חדש')} onClick={onRotate}>↻ {text('NEW', 'חדש')}</button></section>
}

function RoomCodeConfirmation({
  code,
  title,
  description,
  details,
  warning,
  confirmLabel,
  confirmDisabled = false,
  pending = false,
  onCancel,
  onConfirm,
}: {
  code: string
  title: string
  description: string
  details?: string
  warning?: string
  confirmLabel: string
  confirmDisabled?: boolean
  pending?: boolean
  onCancel: () => void
  onConfirm: (confirmedCode: string) => boolean | void | Promise<boolean | void>
}) {
  const [confirmedCode, setConfirmedCode] = useState('')
  const [submitFailed, setSubmitFailed] = useState(false)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const matches = normalizeRoomCode(confirmedCode) === code
  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog && !dialog.open) dialog.showModal()
    return () => {
      if (dialog?.open) dialog.close()
    }
  }, [])
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!matches || confirmDisabled || pending) return
    setSubmitFailed(false)
    try {
      const result = await onConfirm(confirmedCode)
      if (result === false) setSubmitFailed(true)
    } catch {
      setSubmitFailed(true)
    }
  }
  return (
    <dialog ref={dialogRef} className="confirm-dialog" aria-labelledby="confirm-title" onCancel={(event) => { event.preventDefault(); if (!pending) onCancel() }}>
        <h2 id="confirm-title">{title}</h2>
        <p>{description}</p>
        {details && <p className="confirm-details">{details}</p>}
        {warning && <p className="confirm-warning" role="alert">{warning}</p>}
        <form onSubmit={submit}>
          <label><span>{text(`Type ${code} to confirm`, `הקלידו ${code} לאישור`)}</span><input className="code-input" value={confirmedCode} onChange={(event) => { setConfirmedCode(event.target.value); setSubmitFailed(false) }} aria-label={text('Current room code confirmation', 'אישור קוד החדר הנוכחי')} placeholder={code} maxLength={4} autoCapitalize="characters" autoFocus /></label>
          {submitFailed && <p className="dialog-error" role="alert">{text('That action did not go through. Check the connection and try again.', 'הפעולה לא הושלמה. בדקו את החיבור ונסו שוב.')}</p>}
          <div className="confirm-actions"><button type="button" className="button button--ghost" disabled={pending} onClick={onCancel}>{text('CANCEL', 'ביטול')}</button><button className="button button--danger" disabled={!matches || confirmDisabled || pending}>{pending ? text('WORKING…', 'מבצעים…') : confirmLabel}</button></div>
        </form>
    </dialog>
  )
}

function PlayerSeat({ player, game, index }: { player: Player; game: Game; index: number }) {
  const folded = player.foldedHand === game.handNumber
  return <article className={`seat ${folded ? 'seat--folded' : ''}`} style={{ '--seat-index': index } as CSSProperties}><span className="avatar">{initials(player.name)}</span><div><b>{player.name}{player.uid === game.leaderUid && <em className="seat-leader">♛</em>}</b><strong>{formatChips(player.balance)}</strong></div><small>{folded ? text('FOLD', 'כיפול') : player.balance === 0 ? text('0 CHIPS', '0 צ׳יפים') : `● ${text('ACTIVE', 'פעיל')}`}</small></article>
}

function PlayersList({ game, players }: { game: Game; players: Player[] }) {
  return <section className="side-card"><h2><Icon name="users"/>{text('PLAYERS', 'שחקנים')} <span>{players.length}</span></h2><div className="players-list">{players.map((player) => { const folded = player.foldedHand === game.handNumber; return <div key={player.uid} className={folded ? 'muted' : ''}><span className="avatar avatar--small">{initials(player.name)}</span><b>{player.name}<small>{folded ? text('FOLDED', 'פרש/ה') : text('IN HAND', 'ביד')}</small></b><strong>{formatChips(player.balance)}</strong></div> })}</div></section>
}

function ActionLog({ actions }: { actions: GameAction[] }) {
  const listRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: reducedMotion ? 'auto' : 'smooth' })
  }, [actions.length])
  return <section className="side-card action-log"><h2><Icon name="log"/>{text('ACTION LOG', 'יומן פעולות')}</h2><div className="log-list" ref={listRef}>{actions.length ? actions.map((action) => <div className="log-item" key={action.id}><span className={`log-icon log-icon--${action.type}`}>{actionIcon(action.type)}</span><p>{actionText(action)}<small>{relativeActionTime(action)}</small></p></div>) : <p className="empty-log">{text('ACTIONS APPEAR HERE', 'הפעולות יופיעו כאן')}</p>}</div></section>
}

function TableControls({
  services,
  game,
  players,
  actions,
  run,
  tableDisplayName,
  onStartOver,
  compact = false,
}: Omit<ViewProps, 'member' | 'isTableAuthority'> & { tableDisplayName: string; onStartOver: (session: GameSession) => void; compact?: boolean }) {
  const [open, setOpen] = useState(false)
  const [targetUid, setTargetUid] = useState(players[0]?.uid ?? '')
  const [correction, setCorrection] = useState('')
  const [correctionType, setCorrectionType] = useState<'player' | 'pot'>('player')
  const [confirmation, setConfirmation] = useState<'reset-hand' | 'restart-game' | 'start-over' | null>(null)
  const [confirmationPending, setConfirmationPending] = useState(false)
  const selectedUid = players.some((player) => player.uid === targetUid) ? targetUid : (players[0]?.uid ?? '')
  const selected = players.find((player) => player.uid === selectedUid)
  const undo = getUndoEligibility(game, actions, players)
  const resetSummary = getHandResetSummary(game, players)

  const award = () => selected && run(() => awardPot(services.db, game.code, services.user.uid, selected.uid), uiMessage(`Pot awarded to ${selected.name}`, `הקופה הוענקה ל${selected.name}`))
  const nextHand = () => run(() => startNewHand(services.db, game.code, services.user.uid, game.handNumber), uiMessage('New hand started', 'יד חדשה התחילה'))
  const nextRound = () => run(() => startNextBettingRound(services.db, game.code, services.user.uid, bettingRound(game)), uiMessage(`Betting round ${bettingRound(game) + 1} started`, `סבב הימורים ${bettingRound(game) + 1} התחיל`))
  const undoLatest = () => run(() => undoLastAction(services.db, game.code, services.user.uid), uiMessage('Undo logged', 'הביטול נרשם ביומן'))
  const resetHand = async (confirmedCode: string) => {
    setConfirmationPending(true)
    const succeeded = await run(() => resetCurrentHand(services.db, game.code, services.user.uid, confirmedCode).then(() => undefined), uiMessage('Hand reset; contributions refunded', 'היד אופסה; התרומות הוחזרו'))
    setConfirmationPending(false)
    if (succeeded) setConfirmation(null)
    return succeeded
  }
  const startOver = async (confirmedCode: string) => {
    setConfirmationPending(true)
    let nextSession: GameSession | null = null
    const succeeded = await run(async () => {
      const created = await startOverGame(services.db, services.user.uid, game, tableDisplayName, confirmedCode)
      nextSession = { code: created.code, view: 'table', recoveryCode: created.recoveryCode }
    }, uiMessage('Fresh room created; share the new code', 'נוצר חדר חדש; שתפו את הקוד החדש'))
    setConfirmationPending(false)
    if (succeeded && nextSession) onStartOver(nextSession)
    return succeeded
  }
  const restartSamePlayers = async (confirmedCode: string) => {
    setConfirmationPending(true)
    const succeeded = await run(
      () => restartGame(services.db, game.code, services.user.uid, confirmedCode),
      uiMessage('Everyone returned to the lobby', 'כולם חזרו ללובי'),
    )
    setConfirmationPending(false)
    if (succeeded) setConfirmation(null)
    return succeeded
  }
  const correct = async () => {
    const delta = Number(correction)
    if (!Number.isSafeInteger(delta) || delta === 0) return
    if (correctionType === 'player' && selected) await run(() => adjustBalance(services.db, game.code, services.user.uid, selected.uid, delta), uiMessage('Player balance corrected', 'יתרת השחקן תוקנה'))
    if (correctionType === 'pot') await run(() => adjustPot(services.db, game.code, services.user.uid, delta), uiMessage('Pot corrected', 'הקופה תוקנה'))
    setCorrection('')
    setOpen(false)
  }

  return (
    <section className={`host-controls ${compact ? 'host-controls--compact' : ''}`}>
      <div className="host-title"><span><Icon name="settings"/><b>{text('TABLE CONTROL', 'בקרת שולחן')}</b></span></div>
      <LeaderAssignmentControl services={services} game={game} players={players} run={run}/>
      <div className="host-row">
        <select aria-label={text('Choose winner', 'בחירת זוכה')} value={selectedUid} onChange={(event) => setTargetUid(event.target.value)}>{players.map((player) => <option value={player.uid} key={player.uid}>{player.name}</option>)}</select>
        <button className="button button--gold" aria-label={text(`Award pot to ${selected?.name ?? 'winner'}`, `הענקת הקופה ל${selected?.name ?? 'זוכה'}`)} disabled={!selected || game.pot === 0 || game.handOpen === false} onClick={award}>🏆 {selected?.name ?? text('WINNER', 'זוכה')}</button>
        <button className="button button--outline" aria-label={text('Start a new betting round', 'התחלת סבב הימורים חדש')} disabled={game.handOpen === false} onClick={nextRound}>◉ {text('NEW ROUND', 'סבב חדש')}</button>
        <button className="button button--outline" aria-label={text('Start new hand', 'התחלת יד חדשה')} disabled={game.handOpen !== false || game.pot !== 0} onClick={nextHand}>↻ {text('NEW HAND', 'יד חדשה')}</button>
        {ENABLE_TABLE_UNDO && <button className="button button--ghost" aria-label={text('Undo latest safe action', 'ביטול הפעולה הבטוחה האחרונה')} title={undo.reason} disabled={!undo.allowed} onClick={undoLatest}>↶ {text('UNDO', 'ביטול')}</button>}
        <button className="button button--ghost" aria-label={text('Open table correction', 'פתיחת תיקון שולחן')} onClick={() => setOpen(!open)}>± {text('FIX', 'תיקון')}</button>
      </div>
      <div className="safety-row">
        <button className="button button--ghost" disabled={game.pot === 0} onClick={() => setConfirmation('reset-hand')}>⟲ {text('RESET HAND', 'איפוס יד')}</button>
        <button className="button button--danger-outline" onClick={() => setConfirmation('restart-game')}>⟲ {text('RETURN TO LOBBY', 'חזרה ללובי')}</button>
        <button className="button button--ghost" onClick={() => setConfirmation('start-over')}>＋ {text('FRESH ROOM', 'חדר חדש')}</button>
      </div>
      {ENABLE_TABLE_UNDO && <p className={`undo-reason ${undo.allowed ? 'undo-reason--ready' : ''}`}>{undo.reason}</p>}
      {open && <div className="correction-panel"><div className="segmented"><button className={correctionType === 'player' ? 'active' : ''} onClick={() => setCorrectionType('player')}>{text('PLAYER', 'שחקן')}</button><button className={correctionType === 'pot' ? 'active' : ''} onClick={() => setCorrectionType('pot')}>{text('POT', 'קופה')}</button></div>{correctionType === 'player' && <select aria-label={text('Player to correct', 'שחקן לתיקון')} value={selectedUid} onChange={(event) => setTargetUid(event.target.value)}>{players.map((player) => <option value={player.uid} key={player.uid}>{player.name}</option>)}</select>}<input aria-label={text('Correction amount', 'סכום תיקון')} value={correction} onChange={(event) => setCorrection(event.target.value.replace(/[^0-9-]/g, ''))} inputMode="numeric" placeholder="+100 / -50" dir="ltr"/><button className="button button--primary" onClick={correct}>{text('SAVE', 'שמירה')}</button></div>}
      {confirmation === 'reset-hand' && <RoomCodeConfirmation code={game.code} title={text('RESET THIS HAND?', 'לאפס את היד הזאת?')} description={text('Every current contribution will be refunded, folds cleared, the pot set to 0, and a new hand opened. The action stays in the log.', 'כל התרומות ביד יוחזרו, הכיפולים יימחקו, הקופה תאופס ותיפתח יד חדשה. הפעולה תישאר ביומן.')} details={text(`Refund ${formatChips(resetSummary.refundAmount)} from the pot to ${resetSummary.playerCount} contributing players.`, `החזרת ${formatChips(resetSummary.refundAmount)} מהקופה ל־${resetSummary.playerCount} שחקנים שתרמו.`)} warning={!resetSummary.matchesPot ? text('Pot and contributions do not match. Use FIX or START OVER; nothing will be changed.', 'הקופה והתרומות אינן תואמות. השתמשו בתיקון או בהתחלה מחדש; שום דבר לא ישתנה.') : undefined} confirmLabel={text('RESET & REFUND', 'איפוס והחזרה')} confirmDisabled={!resetSummary.matchesPot} pending={confirmationPending} onCancel={() => setConfirmation(null)} onConfirm={resetHand} />}
      {confirmation === 'restart-game' && <RoomCodeConfirmation code={game.code} title={text('RETURN EVERYONE TO THE LOBBY?', 'להחזיר את כולם ללובי?')} description={text('All balances reset to the starting stack. The same players and Leader stay in the room.', 'כל היתרות יחזרו לכמות ההתחלתית. אותם שחקנים והמוביל/ה יישארו בחדר.')} details={text('The current pot and hand are cleared. The complete action log stays saved.', 'הקופה והיד הנוכחיות יימחקו. יומן הפעולות המלא יישמר.')} confirmLabel={text('RESET & RETURN TO LOBBY', 'איפוס וחזרה ללובי')} pending={confirmationPending} onCancel={() => setConfirmation(null)} onConfirm={restartSamePlayers} />}
      {confirmation === 'start-over' && <RoomCodeConfirmation code={game.code} title={text('CREATE A FRESH ROOM?', 'ליצור חדר חדש?')} description={text('A new code will use the same game name and starting chips. Everyone must join again.', 'קוד חדש ישתמש באותו שם משחק ובאותה כמות פתיחה. כולם יצטרכו להצטרף שוב.')} details={text('The current room, balances, and complete action log stay saved and unchanged.', 'החדר הנוכחי, היתרות ויומן הפעולות המלא נשארים שמורים וללא שינוי.')} confirmLabel={text('CREATE FRESH ROOM', 'יצירת חדר חדש')} pending={confirmationPending} onCancel={() => setConfirmation(null)} onConfirm={startOver} />}
    </section>
  )
}

export default function App() {
  if (location.pathname === '/__table' || location.pathname.startsWith('/__table/')) {
    return <Suspense fallback={<main className="splash"><Spinner label="LOADING CARD TABLE…"/></main>}><StandaloneTablePage/></Suspense>
  }
  if (location.pathname === '/__harness' || location.pathname.startsWith('/__harness/')) {
    return <Suspense fallback={<main className="splash"><Spinner label="LOADING TEST DEALER…"/></main>}><TestHarnessPage/></Suspense>
  }
  return <LanguageProvider><AppContent /></LanguageProvider>
}
